import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { CheckCircleOutline, Close, ErrorOutline, SelfImprovement } from '@mui/icons-material';
import AnimatedCheckIcon from 'components/shared/toasts/AnimatedCheckIcon';
import PlayRecoveryRunner from 'components/play/PlayRecoveryRunner';
import { fallbackPractices } from 'components/play/recovery/practiceUtils';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { PENDING_WELLBEING_CHECKIN, SUBMIT_WELLBEING_CHECKIN } from 'api/graphql/wellbeing';
import routes from 'common/routes';

// 5 opciones fijas del check-in (1 = peor, 5 = mejor). El emoji ES la interfaz.
const OPTIONS = [
  { value: 1, emoji: '😞' },
  { value: 2, emoji: '😕' },
  { value: 3, emoji: '😐' },
  { value: 4, emoji: '🙂' },
  { value: 5, emoji: '😄' }
];

// Cada slot es el día en que abrió la pregunta. Mostrarlo evita que el usuario lea las
// preguntas acumuladas como "la app me está preguntando de más".
const SLOT_DAYS = { 1: 'Lunes', 2: 'Miércoles', 3: 'Viernes' };

const stepIn = keyframes`
  from { transform: translateY(14px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const sortSlots = (slots) => [...(slots || [])].sort((a, b) => a.slot - b.slot);

/**
 * Bienestar Corporativo — micro check-in: 3 por semana (lunes/miércoles/viernes) de UNA
 * pregunta cada uno. Lo que abrió y no se respondió espera dentro de la misma semana, así que
 * aquí pueden caer 1, 2 o 3 preguntas: se muestran TODAS en una pasada y se envían en un solo
 * submit con el slot de cada respuesta. Al terminar, recomendación inmediata (regla simple)
 * con la práctica de recuperación lista para ejecutar aquí mismo.
 */
function WellbeingCheckinModal({ open, pending = null, onClose }) {
  const { t } = useTranslation(['wellbeing']);
  const navigate = useNavigate();
  const client = useApolloClient();
  const blockRefs = useRef({});
  const submitRef = useRef(null);
  // El `disabled` del botón depende de un re-render; este cerrojo corta el segundo envío en el
  // mismo tick (doble tap), que si perdía la carrera pisaba el resultado bueno con uno vacío.
  const inFlightRef = useRef(false);

  const [slots, setSlots] = useState([]);
  // { [slot]: { value, questionId } }. El `questionId` NO es decorativo: si la pregunta de un
  // slot cambia (cruce de semana ISO, o el admin desactiva la pregunta) la respuesta vieja deja
  // de ser válida y hay que descartarla, no reenviarla contra otra pregunta.
  const [answers, setAnswers] = useState({});
  const [weekKey, setWeekKey] = useState(null);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [runnerPractice, setRunnerPractice] = useState(null);

  const [submit, { loading: saving }] = useMutation(SUBMIT_WELLBEING_CHECKIN, {
    refetchQueries: [{ query: PENDING_WELLBEING_CHECKIN }]
  });

  // `pending` se lee por ref para que el snapshot de abajo dependa SOLO de `open`: si el
  // efecto dependiera del objeto, cada refetch en segundo plano borraría lo ya respondido.
  const pendingRef = useRef(pending);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  // La lista se CONGELA al abrir: `pending` se refresca en segundo plano (cache-and-network) y
  // sin este snapshot un refetch le cambiaría las preguntas al usuario a mitad del check-in.
  useEffect(() => {
    if (!open) return;
    inFlightRef.current = false;
    setSlots(sortSlots(pendingRef.current?.slots));
    setWeekKey(pendingRef.current?.week_key || null);
    setAnswers({});
    setResult(null);
    setFailure(null);
    setRunnerPractice(null);
  }, [open]);

  const total = slots.length;
  // Se exige que la respuesta sea de LA pregunta que hoy tiene ese slot: una respuesta
  // heredada de una pregunta que ya no aplica no puede contar como "respondida".
  const answeredCount = slots.filter((s) => answers[s.slot]?.questionId === s.question.id).length;
  const missing = total - answeredCount;
  const done = Boolean(result);

  const dayLabel = (slot) => t(`slotDays.${slot}`, { ns: 'wellbeing', defaultValue: SLOT_DAYS[slot] || '' });

  const questionLabel = (question) =>
    t(`questions.${question.code}`, { ns: 'wellbeing', defaultValue: question.code });

  // Tras responder, la siguiente pregunta sin contestar se trae a la vista: con 2-3 bloques en
  // un diálogo `xs` la de abajo queda fuera de pantalla y parece que no hay nada más. Cuando ya
  // no queda ninguna se baja al CTA: con 3 preguntas el botón queda bajo el pliegue y el usuario
  // se quedaba mirando la última respuesta sin ver que ya podía enviar.
  const revealNextUnanswered = (nextAnswers) => {
    const next = slots.find((s) => nextAnswers[s.slot]?.questionId !== s.question.id);
    const element = next ? blockRefs.current[next.slot] : submitRef.current;
    if (!element?.scrollIntoView) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    element.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
  };

  const handleAnswer = (slot, questionId, value) => {
    if (saving) return;
    const next = { ...answers, [slot]: { value, questionId } };
    setAnswers(next);
    setFailure(null);
    window.requestAnimationFrame(() => revealNextUnanswered(next));
  };

  const handleSubmit = async () => {
    if (saving || inFlightRef.current || total === 0 || missing > 0) return;
    inFlightRef.current = true;
    setFailure(null);

    // El payload se arma desde los slots PENDIENTES, no desde `answers`: tras un envío a
    // medias `answers` conserva lo ya guardado y reenviarlo chocaría con el unique
    // (user_id, week_key, slot).
    const payload = slots.map((s) => ({
      slot: s.slot,
      question_id: s.question.id,
      value: answers[s.slot].value
    }));

    try {
      const { data } = await submit({ variables: { answers: payload } });
      setResult(data?.submitWellbeingCheckin || { recommendations: [] });
    } catch (error) {
      // El envío puede quedar a medias (un slot guardado y el otro no). La única fuente de
      // verdad de qué falta es el backend, así que se le vuelve a preguntar en vez de adivinar.
      // eslint-disable-next-line no-console
      console.error('submitWellbeingCheckin', error);

      let remaining = slots;
      let freshWeek = weekKey;
      try {
        const { data: fresh } = await client.query({
          query: PENDING_WELLBEING_CHECKIN,
          fetchPolicy: 'network-only'
        });
        remaining = sortSlots(fresh?.pendingWellbeingCheckin?.slots);
        freshWeek = fresh?.pendingWellbeingCheckin?.week_key || null;
      } catch {
        remaining = slots; // Ni el refetch salió: se asume que no se guardó nada.
        freshWeek = weekKey;
      }

      // Cruce de la semana ISO con el modal abierto (domingo 23:5x → lunes): el backend rechaza
      // TODO el envío porque las preguntas de la semana nueva son otras. Nada se guardó, así que
      // ni se puede decir "guardamos parte" ni se pueden reutilizar los valores anteriores.
      // Sin dato de semana no se asume caducidad: en la duda se conserva lo respondido (borrarlo
      // por un fallo de red sería peor que un mensaje impreciso).
      const sameWeek = !freshWeek || !weekKey || freshWeek === weekKey;

      if (sameWeek && remaining.length === 0) {
        // Sí quedó guardado todo (el error fue de red al leer la respuesta): se cierra bien,
        // solo sin recomendación, en vez de pedirle al usuario que responda dos veces.
        setResult({ recommendations: [] });
        return;
      }

      // Solo sobreviven las respuestas cuyo slot sigue pendiente CON la misma pregunta. Sin
      // esto se reenviaba el valor de la pregunta vieja contra el id de la nueva y quedaba
      // registrado como respuesta a algo que el usuario nunca leyó.
      const kept = {};
      remaining.forEach((s) => {
        const answer = answers[s.slot];
        if (answer && answer.questionId === s.question.id) kept[s.slot] = answer;
      });

      setSlots(remaining);
      setWeekKey(freshWeek || weekKey);
      setAnswers(kept);
      setFailure({
        expired: !sameWeek,
        partial: sameWeek && remaining.length < total,
        remaining: remaining.length
      });
    } finally {
      inFlightRef.current = false;
    }
  };

  // Mapea la clave de recomendación a la práctica de recuperación (universal: sirve para
  // usuarios Play y no-Play; son respiración/movilidad, no fuerza).
  const openRecommendation = (key) => {
    if (key === 'generalContent') {
      onClose();
      navigate(routes.listNews.path);
      return;
    }
    const practice = fallbackPractices(t).find((p) => p.key === key) || null;
    if (practice) setRunnerPractice(practice);
  };

  const recommendationLabel = (key) =>
    t(`recommendations.${key}`, {
      ns: 'wellbeing',
      defaultValue:
        {
          boxBreathing: 'Respiración cuadrada (5 min)',
          breathing478: 'Respiración 4-7-8 (4 min)',
          mobility: 'Movilidad exprés (8 min)',
          generalContent: 'Contenido de bienestar'
        }[key] || key
    });

  // Tres desenlaces distintos de un envío fallido, cada uno con su instrucción: la semana
  // cambió (hay preguntas nuevas), se guardó una parte, o no se guardó nada.
  const failureBody = () => {
    if (failure?.expired) {
      return t('submitErrorExpired', {
        ns: 'wellbeing',
        defaultValue: 'La semana cambió mientras respondías. Estas son las preguntas nuevas.'
      });
    }
    if (failure?.partial) {
      return t('submitErrorPartial', {
        ns: 'wellbeing',
        count: failure.remaining,
        defaultValue: 'Guardamos parte de tus respuestas. Quedan {{count}} preguntas por enviar.',
        defaultValue_one: 'Guardamos parte de tus respuestas. Queda 1 pregunta por enviar.'
      });
    }
    return t('submitErrorBody', {
      ns: 'wellbeing',
      defaultValue: 'Revisa tu conexión y vuelve a intentarlo. Tus respuestas siguen aquí.'
    });
  };

  const submitLabel = () => {
    if (saving) return t('sending', { ns: 'wellbeing', defaultValue: 'Enviando…' });
    if (missing > 0) {
      return t('submitMissing', {
        ns: 'wellbeing',
        count: missing,
        defaultValue: 'Faltan {{count}} preguntas',
        defaultValue_one: 'Falta 1 pregunta'
      });
    }
    return t('submitCta', {
      ns: 'wellbeing',
      count: total,
      defaultValue: 'Enviar respuestas',
      defaultValue_one: 'Enviar respuesta'
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'surface.content',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'hairline.main'
          }
        }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap>
              {done
                ? t('doneTitle', { ns: 'wellbeing', defaultValue: '¡Gracias!' })
                : t('title', { ns: 'wellbeing', defaultValue: '¿Cómo vas esta semana?' })}
            </Typography>
            {!done && total > 1 && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                aria-live="polite">
                {t('progress', {
                  ns: 'wellbeing',
                  answered: answeredCount,
                  total,
                  defaultValue: '{{answered}} de {{total}} respondidas'
                })}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('close', { ns: 'wellbeing', defaultValue: 'Cerrar' })}
            sx={{ color: 'text.secondary', flex: 'none' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <DialogContent>
          {!done && total === 0 && (
            <Stack spacing={1} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('emptyPending', {
                  ns: 'wellbeing',
                  defaultValue: 'No tienes preguntas pendientes por ahora.'
                })}
              </Typography>
            </Stack>
          )}

          {!done && total > 0 && (
            <Stack spacing={2.5} sx={{ py: 1, animation: `${stepIn} 250ms cubic-bezier(0.32, 0.72, 0, 1)` }}>
              {/* Quien entra el viernes ve 3 preguntas juntas: hay que decirle por qué. */}
              {total > 1 && (
                <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center' }}>
                  {t('catchUpNote', {
                    ns: 'wellbeing',
                    defaultValue: 'Son las de esta semana que quedaron sin responder.'
                  })}
                </Typography>
              )}

              {slots.map((item) => {
                const answer = answers[item.slot];
                const selected = answer?.questionId === item.question.id ? answer.value : null;
                const labelId = `wellbeing-question-${item.slot}`;

                return (
                  <Box
                    key={item.slot}
                    ref={(element) => {
                      blockRefs.current[item.slot] = element;
                    }}
                    sx={{
                      p: 2,
                      borderRadius: '16px',
                      border: '1px solid',
                      borderColor: selected ? 'gold.main' : 'hairline.main',
                      backgroundColor: 'surface.raised',
                      transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1)'
                    }}>
                    {total > 1 && (
                      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: selected ? 'gold.main' : 'text.disabled',
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase'
                          }}>
                          {dayLabel(item.slot)}
                        </Typography>
                        {Boolean(selected) && <CheckCircleOutline sx={{ fontSize: 14, color: 'gold.main' }} />}
                      </Stack>
                    )}

                    <Typography
                      id={labelId}
                      variant="subtitle1"
                      fontWeight={800}
                      align="center"
                      sx={{ letterSpacing: '-0.01em', mb: 2, lineHeight: 1.3 }}>
                      {questionLabel(item.question)}
                    </Typography>

                    {/* 5 emojis grandes: tap = respuesta. El envío es explícito porque ahora
                        pueden quedar otras preguntas debajo sin contestar. */}
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      role="radiogroup"
                      aria-labelledby={labelId}
                      sx={{ width: '100%' }}>
                      {OPTIONS.map((option) => (
                        <Box
                          key={option.value}
                          role="radio"
                          tabIndex={0}
                          aria-checked={selected === option.value}
                          aria-label={t(`scale.${option.value}`, { ns: 'wellbeing', defaultValue: String(option.value) })}
                          onClick={() => handleAnswer(item.slot, item.question.id, option.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleAnswer(item.slot, item.question.id, option.value);
                            }
                          }}
                          sx={{
                            flex: 1,
                            maxWidth: 60,
                            aspectRatio: '1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.8rem',
                            borderRadius: '14px',
                            border: '1px solid',
                            borderColor: selected === option.value ? 'gold.main' : 'hairline.main',
                            backgroundColor: selected === option.value ? 'gold.soft' : 'surface.content',
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? 'default' : 'pointer',
                            transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1), border-color 200ms, opacity 200ms',
                            '&:hover': { transform: 'translateY(-3px)', borderColor: 'hairline.strong' },
                            '&:active': { transform: 'scale(0.92)' },
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
                          }}>
                          {option.emoji}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                );
              })}

              {/* Error inline: el detalle técnico se queda en el log, aquí va qué pasó y qué
                  hacer. Un toast que se va no basta cuando hay respuestas a medio guardar. */}
              {failure && (
                <Stack
                  direction="row"
                  spacing={1.25}
                  role="alert"
                  sx={{
                    p: 1.5,
                    borderRadius: '14px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.overlay'
                  }}>
                  <ErrorOutline sx={{ fontSize: 20, color: 'sessionStatus.absent.fg', flex: 'none', mt: 0.25 }} />
                  <Box>
                    <Typography variant="caption" fontWeight={800} component="p">
                      {t('submitErrorTitle', { ns: 'wellbeing', defaultValue: 'No pudimos guardar tu check-in' })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {failureBody()}
                    </Typography>
                  </Box>
                </Stack>
              )}

              <Button
                fullWidth
                ref={submitRef}
                onClick={handleSubmit}
                disabled={missing > 0 || saving}
                sx={{ ...premiumButtonSx, py: 1.1 }}>
                {submitLabel()}
              </Button>

              <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center' }}>
                {t('privacyNote', {
                  ns: 'wellbeing',
                  defaultValue: 'Tu empresa solo ve datos agregados y anónimos, nunca tus respuestas.'
                })}
              </Typography>
            </Stack>
          )}

          {done && (
            <Stack spacing={2.5} alignItems="center" sx={{ py: 2, textAlign: 'center', animation: `${stepIn} 250ms cubic-bezier(0.32, 0.72, 0, 1)` }}>
              <Box sx={{ transform: 'scale(2)', py: 1 }}>
                <AnimatedCheckIcon />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 300 }}>
                {t('doneBody', {
                  ns: 'wellbeing',
                  defaultValue: 'Check-in registrado. Nos vemos en el próximo (lunes, miércoles y viernes).'
                })}
              </Typography>

              {/* De la medición a la acción: la práctica recomendada, lista para ejecutar. */}
              {(result?.recommendations || []).length > 0 && (
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    borderRadius: '14px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.overlay',
                    textAlign: 'left'
                  }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <SelfImprovement sx={{ color: 'gold.main', fontSize: 20 }} />
                    <Typography variant="caption" fontWeight={800}>
                      {t('recommendationTitle', { ns: 'wellbeing', defaultValue: 'Para ti, ahora mismo' })}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {result.recommendations.map((key) => (
                      <Button
                        key={key}
                        size="small"
                        onClick={() => openRecommendation(key)}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontWeight: 700,
                          color: 'gold.main',
                          border: '1px solid',
                          borderColor: 'hairline.main',
                          borderRadius: 2,
                          px: 1.5,
                          '&:active': { transform: 'scale(0.98)' }
                        }}>
                        {recommendationLabel(key)}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              <Button fullWidth onClick={onClose} sx={{ ...premiumButtonSx, py: 1.1 }}>
                {t('close', { ns: 'wellbeing', defaultValue: 'Cerrar' })}
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* La práctica recomendada corre AQUÍ mismo (runner de recuperación universal). */}
      <PlayRecoveryRunner
        practice={runnerPractice}
        open={Boolean(runnerPractice)}
        onClose={() => setRunnerPractice(null)}
      />
    </>
  );
}

WellbeingCheckinModal.propTypes = {
  open: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  pending: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default WellbeingCheckinModal;
